import React, { useState, useEffect } from 'react';
import './BrandGuidelines.css';
import { CheckCircle } from 'lucide-react';
import AIheader from '../RentAI/AIheader';
import Footer from '../../Component/Footer/Footer';

// Navigation items defined outside component to avoid re-renders
const navItems = [
  { id: "our-brand", label: "Our Brand" },
  { id: "services", label: "Services" },
  { id: "products", label: "Products" },
  { id: "logo", label: "Logo" },
  { id: "naming", label: "Naming" },
  { id: "color", label: "Color" },
  { id: "typography", label: "Typography" },
  { id: "buttons", label: "Buttons" },
  { id: "compliance", label: "Compliance" },
];

const BrandGuidelines = () => {
  return (
    <>
      <AIheader />
      
      <div className="brand-guidelines-page">
        <Sidebar />
        <MobileNav />
        
        <main className="brand-guidelines-main">
          <HeroSection />
          <BrandIntroSection />
          <ServicesSection />
          <ProductsSection />
          <LogoSection />
          <NamingSection />
          <ColorSection />
          <TypographySection />
          <ButtonsSection />
          <ComplianceSection />
          <CTASection />
          <BrandFooter />
        </main>
      </div>
      
      <Footer />
    </>
  );
};

// Sidebar Component
const Sidebar = () => {
  const [activeSection, setActiveSection] = useState("our-brand");

  useEffect(() => {
    const handleScroll = () => {
      const sections = navItems.map(item => document.getElementById(item.id));
      const scrollPosition = window.scrollY + 200;

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(navItems[i].id);
          break;
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  };

  return (
    <aside className="brand-sidebar">
      <div className="brand-sidebar-logo">
        <span className="brand-logo-text">rentid</span>
      </div>
      
      <nav className="brand-sidebar-nav">
        <ul>
          {navItems.map((item) => (
            <li key={item.id}>
              <button
                onClick={() => scrollToSection(item.id)}
                className={`brand-nav-button ${activeSection === item.id ? 'active' : ''}`}
              >
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      </nav>

      <div className="brand-sidebar-footer">
        <a
          href="https://drive.google.com/drive/u/1/folders/1ZeRG1v5WYe_eHni5p4HweYp6dy8k15a6"
          target="_blank"
          rel="noopener noreferrer"
          className="brand-download-link"
        >
          <svg className="download-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          Download Assets
        </a>
      </div>
    </aside>
  );
};

// Mobile Navigation Component
const MobileNav = () => {
  const [isOpen, setIsOpen] = useState(false);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
    setIsOpen(false);
  };

  return (
    <div className="brand-mobile-nav">
      <div className="brand-mobile-header">
        <div className="brand-mobile-logo">rentid</div>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="brand-mobile-toggle"
          aria-label="Toggle menu"
        >
          {isOpen ? (
            <svg className="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg className="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          )}
        </button>
      </div>
      
      {isOpen && (
        <nav className="brand-mobile-menu">
          <ul>
            {navItems.map((item) => (
              <li key={item.id}>
                <button
                  onClick={() => scrollToSection(item.id)}
                  className="brand-mobile-nav-button"
                >
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
          <div className="brand-mobile-menu-footer">
            <a
              href="https://drive.google.com/drive/u/1/folders/1ZeRG1v5WYe_eHni5p4HweYp6dy8k15a6"
              target="_blank"
              rel="noopener noreferrer"
              className="brand-mobile-download"
            >
              <svg className="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download Assets
            </a>
          </div>
        </nav>
      )}
    </div>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section className="brand-hero">
      <div className="brand-hero-content">
        <div className="brand-hero-logo">rentid</div>
      </div>
    </section>
  );
};

// Brand Intro Section
const BrandIntroSection = () => {
  return (
    <section id="our-brand" className="brand-section">
      <div className="brand-container">
        <div className="brand-card">
          <h2 className="brand-section-title">Brand Guidelines</h2>
          
          <div className="brand-intro-layout">
            <div className="brand-intro-text">
              <p className="brand-text-lg">
                This brand guide is designed to help you better understand Rentid, our brand culture, and the way we present ourselves across marketing, sales, product, and partnership materials. It serves as a foundation for how we communicate, how we show up visually, and how we create a consistent experience across every touchpoint.
              </p>
            </div>
            
            <div className="brand-guide-card">
              <div className="brand-guide-content">
                <span className="brand-guide-logo">rentid</span>
                <span className="brand-guide-text">
                  BRAND<br/>
                  GUIDE
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="brand-two-panel">
          <div className="brand-panel brand-panel-light">
            <h3 className="brand-panel-title">What we built</h3>
            <p className="brand-panel-text">
              An all-in-one, AI-native vehicle rental commerce platform and API suite powering the next generation of SMB and enterprise rental businesses.
            </p>
          </div>
          <div className="brand-panel brand-panel-dark">
            <h3 className="brand-panel-title">Our mission</h3>
            <p className="brand-panel-text">
              We empower mobility operators with the solutions needed to build, manage and scale with tools and automated workflows.
            </p>
          </div>
        </div>

        <div className="brand-card">
          <span className="brand-eyebrow">Introduction</span>
          <h3 className="brand-subsection-title">Our Personality</h3>
          
          <div className="brand-two-col">
            <div>
              <p className="brand-text">
                Rentid's brand personality is industry experts, operator-first, modern, trustworthy, and infrastructure-minded.
              </p>
              <p className="brand-text">
                We speak with clarity, confidence, and practical expertise because we understand the rental industry from the inside.
              </p>
            </div>
            <div>
              <h4 className="brand-list-title">Brand Traits</h4>
              <ul className="brand-trait-list">
                <li><span className="bullet"></span>Industry experts</li>
                <li><span className="bullet"></span>Operator-first</li>
                <li><span className="bullet"></span>Modern</li>
                <li><span className="bullet"></span>Trustworthy</li>
                <li><span className="bullet"></span>Infrastructure-minded</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="brand-card">
          <h3 className="brand-subsection-title">Brand Voice</h3>
          
          <div className="brand-voice-box">
            <h4 className="brand-voice-title">Brand Voice</h4>
            <p className="brand-text">Rentid communication should feel:</p>
            <ul className="brand-check-list">
              <li>
                <div className="check-icon">✓</div>
                <span>Professional</span>
              </li>
              <li>
                <div className="check-icon">✓</div>
                <span>Transparent</span>
              </li>
              <li>
                <div className="check-icon">✓</div>
                <span>Operator-focused</span>
              </li>
            </ul>
            <p className="brand-text">
              Our audience are comprised of <strong>SMB and Enterprise fleet operators.</strong>
            </p>
          </div>
        </div>

        <div className="brand-card">
          <h3 className="brand-subsection-title">Brand Positioning</h3>
          
          <div className="brand-two-col">
            <div className="brand-positioning-box brand-positioning-do">
              <h4 className="brand-positioning-title">How Rentid Should Be Positioned</h4>
              <p className="brand-text-sm">Correct descriptions:</p>
              <ul className="brand-check-list">
                <li>
                  <div className="check-icon">✓</div>
                  <span>Rental platform</span>
                </li>
                <li>
                  <div className="check-icon">✓</div>
                  <span>Digital back office</span>
                </li>
                <li>
                  <div className="check-icon">✓</div>
                  <span>Commerce infrastructure</span>
                </li>
              </ul>
            </div>
            
            <div className="brand-positioning-box brand-positioning-dont">
              <h4 className="brand-positioning-title">What Rentid Is NOT</h4>
              <p className="brand-text-sm">Do not describe Rentid as:</p>
              <ul className="brand-check-list">
                <li>
                  <div className="cross-icon">✕</div>
                  <span>A peer-to-peer marketplace</span>
                </li>
                <li>
                  <div className="cross-icon">✕</div>
                  <span>A car sharing platform</span>
                </li>
                <li>
                  <div className="cross-icon">✕</div>
                  <span>An insurance company</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="brand-card">
          <h3 className="brand-card-title">Our Tone</h3>
          <p className="brand-text-lg">
            Our tone is operator-first and assistive — built for rental operators who need technology that helps them manage their back office and provide the best experience for their guests. We speak with clarity, confidence, and practical expertise.
          </p>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    {
      title: "Rental Commerce",
      description: "End-to-end booking, payment, and agreement workflows for rental operators."
    },
    {
      title: "Identity Verification",
      description: "Real-time identity, license, insurance, and background verification."
    },
    {
      title: "Protection & Insurance",
      description: "Commercial coverage, guest protection, claims, and roadside support."
    },
    {
      title: "AI Automation",
      description: "Context-aware AI for booking, operations, and resolution workflows."
    },
    {
      title: "Customer Success",
      description: "Onboarding, resolution, collections, and platform guidance for operators."
    },
    {
      title: "Education",
      description: "Training on operations, compliance, safety, marketing, and growth."
    }
  ];

  return (
    <section id="services" className="brand-section">
      <div className="brand-container">
        <div className="brand-card">
          <span className="brand-eyebrow">Services</span>
          <h3 className="brand-subsection-title">What We Provide</h3>
          <p className="brand-text-lg">
            Rentid provides a complete suite of services that power every aspect of the vehicle rental journey.
          </p>
          
          <div className="brand-services-grid">
            {services.map((service, index) => (
              <div key={index} className="brand-service-card">
                <h4 className="brand-service-title">{service.title}</h4>
                <p className="brand-service-text">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Products Section Component
const ProductsSection = () => {
  const products = [
    {
      name: "RentDirect",
      tagline: "White-labeled rental commerce for modern operators.",
      summary: "RentDirect™ powers direct booking, checkout, rental agreements, payments, calendars, policies, and vehicle availability under the operator's brand.",
      impact: "Gives operators more control over rentals while giving renters a clear, branded booking experience.",
      logo: "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Black_RentDirect_Vertical-cTJlMZ4fpx0B11G59GMVJ092Hnw0n0.png"
    },
    {
      name: "RentID",
      tagline: "Verify before the keys change hands.",
      summary: "RentID™ provides identity verification, license checks, criminal checks, motor vehicle reports, insurance validation, DriverIQ™ scoring, and BlockList screening.",
      impact: "Helps operators make smarter access decisions while creating a safer rental experience for renters.",
      logo: "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Black_rentID_Vertical-mwQalKpd5BWK7sQCf2vbafzviXDbfC.png"
    },
    {
      name: "RentShield",
      tagline: "Protection infrastructure for every rental.",
      summary: "RentShield™ supports On-Rent Coverage, Off-Rent Coverage, Guest Protection Plans, claims, roadside assistance, insurance validation, and vehicle warranty support.",
      impact: "Helps operators manage risk while giving renters clearer protection options during the rental.",
      logo: "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Black_RentShield%20Text%20%2B%20Logo%20%28Verticle%29-DsNUxz4Aypy68Dq2YqyWH08Osnl4KM.png"
    },
    {
      name: "RentAI",
      tagline: "You lead. Rentii acts.",
      summary: "RentAI™ powers Rentii, Rentid's context-aware AI agent for bookings, logistics, verification, administration, operations, and resolution workflows.",
      impact: "Helps operators reduce manual work while giving renters faster answers and better trip support.",
      logo: "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Black_RentAI_Vertical-oWlwZKfmBsJy1VllL79P1c1j7xwrHn.png"
    },
    {
      name: "RentSuccess",
      tagline: "Support that keeps rental operations moving.",
      summary: "RentSuccess™ provides operator onboarding, violation resolution, customer success, platform guidance, collections workflows, and rental agreement support.",
      impact: "Helps operators resolve issues with structure while giving renters a more reliable rental experience.",
      logo: "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Black_RentSuccess_Vertical-lwkmhG9sPlDPRB2Urmd5WYbDMxZ61p.png"
    },
    {
      name: "RentAcademy",
      tagline: "Education for better rental businesses.",
      summary: "RentAcademy™ provides education on platform usage, rental operations, insurance, compliance, safety, claims, marketing, and fleet growth.",
      impact: "Helps operators improve their business while raising the quality of the renter experience.",
      logo: "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Black_RentAcademy_Vertical-a74gwcw5nqF9cxFdidJlbQuRc5Bwqg.png"
    }
  ];

  return (
    <section id="products" className="brand-section">
      <div className="brand-container">
        <div className="brand-card">
          <span className="brand-eyebrow">Products</span>
          <h3 className="brand-subsection-title">Product Suite</h3>
          <p className="brand-text-lg">
            Our products work together to power every stage of the rental journey for operators and renters.
          </p>
          
          <div className="brand-products-grid">
            {products.map((product, index) => (
              <div key={index} className="brand-product-card">
                <div className="brand-product-logo-container">
                  <img
                    src={product.logo}
                    alt={`${product.name} logo`}
                    className="brand-product-logo"
                  />
                </div>
                
                <p className="brand-product-tagline">{product.tagline}</p>
                <p className="brand-product-summary">{product.summary}</p>
                <div className="brand-product-impact">
                  <p className="brand-product-impact-text">
                    <span className="brand-product-impact-label">Impact:</span> {product.impact}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Logo Section (Continued in next part due to length)
const LogoSection = () => {
  const productLogos = [
    {
      name: "RentDirect",
      darkBg: "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Whitelogo_RentDirect_Vertical-iVubQuRrdYPgjSG7rSggus4ylSh444.png",
      lightBg: "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Black_RentDirect_Vertical-SibWPjJ5uxwr7x873FyRTJYHhvAzcf.png",
      darkBgHorizontal: "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Whitelogo_RentDirect_Horizontal-w8l4WguDvUaPFk4FAhfem1fSVreSwl.png",
      lightBgHorizontal: "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Black_RentDirect_Horizontal-l5sJXoUk40BSBiRxW7VQY6PkRuGPYV.png"
    },
    {
      name: "RentID",
      darkBg: "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Whitelogo_rentID_Vertical-GKAW6m22MKQITSsz1Z8ZzVggmqcBIm.png",
      lightBg: "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Black_rentID_Vertical-h1ytpI5ANX9MXuqfPCHlPdEl9I27bS.png",
      darkBgHorizontal: "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Whitelogo_rentID_Horizontal-wHvgKohEBwLQxb4oaVxwQXsLAz7Eqm.png",
      lightBgHorizontal: "/img/black_rentID_horizontal.png"
    },
    {
      name: "RentShield",
      darkBg: "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Whitelogo_RentShield%20Text%20%2B%20Logo%20%28Verticle%29-9AmrTKj6Jor74buZ2egDLyqmZkjhXS.png",
      lightBg: "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Black_RentShield%20Text%20%2B%20Logo%20%28Verticle%29-PVxFtf2zdTujSE2L4YbGaaiYPaypm0.png",
      darkBgHorizontal: "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Whitelogo_RentShield%20Text%20%2B%20Logo%20%28Horizontal%29-gkH2z2Atq4bJEILF1deaE8Bhh2eAoh.png",
      lightBgHorizontal: "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Black_RentShield%20Text%20%2B%20Logo%20%28Horizontal%29-dVavUd72S7yCc9isw3MpEjVVGt0ioh.png"
    },
    {
      name: "RentAI",
      darkBg: "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Whitelogo_RentAI_Vertical-ZgvVHbeSddlevy2C76bifbPekRe1Qa.png",
      lightBg: "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Black_RentAI_Vertical-FahcStYgXgnrMFQAwTG0G1zGM6ltfY.png",
      darkBgHorizontal: "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/White%20logo_RentAI_Horizontal-Qw0CPCmuDAAJ5iSvNPzxNFbfd8gQ6D.png",
      lightBgHorizontal: "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Black_RentAI_Horizontal-MHaESL3AhYeAYU1nDuPZXz7Ai6M3bC.png"
    },
    {
      name: "RentSuccess",
      darkBg: "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Whitelogo_RentSuccess_Vertical-3TJ38s92Pd40BxDZdDNjhNVlxthqvH.png",
      lightBg: "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Black_RentSuccess_Vertical-zqGwqOFvhTyPUaLD9WV7oyPnI4lc52.png",
      darkBgHorizontal: "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Whitelogo_RentSuccess_Horizontal-8xQDLm4JaIoQJuYXLylyUwEvKMicc4.png",
      lightBgHorizontal: "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Black_RentSuccess_Horizontal-JyYz7uDR3gPTqzOJEODzZRYuL4LqyI.png"
    },
    {
      name: "RentAcademy",
      darkBg: "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Whitelogo_RentAcademy_Vertical-sUDfaCCU4iNhAzhLx06Yq8oea2RRVD.png",
      lightBg: "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Black_RentAcademy_Vertical-fAx2HjvyRFChWrPpAMlan7VwnWig9z.png",
      darkBgHorizontal: "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/White%20logo_RentAcademy_Horizontal-kjRfd7I1E497iCx8KhH11wirvQChAK.png",
      lightBgHorizontal: "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Black_RentAcademy_Horizontal-vY62UKqgiE2VgMoflwIvk6xeVExBhZ.png"
    }
  ];

  return (
    <section id="logo" className="brand-section">
      <div className="brand-container">
        <div className="brand-card">
          <span className="brand-eyebrow">Logo</span>
          <h2 className="brand-subsection-title">Our Logo</h2>
          <p className="brand-text-lg">
            The Rentid logo is the primary visual representation of our brand. Use it consistently to maintain brand recognition across all touchpoints.
          </p>
          
          <div className="brand-logo-display">
            <div className="brand-logo-box brand-logo-dark">
              <div className="brand-logo-text-large">rentid</div>
            </div>
            <div className="brand-logo-box brand-logo-light">
              <div className="brand-logo-text-large brand-logo-text-dark">rentid</div>
            </div>
          </div>
          <div className="brand-logo-labels">
            <p>For dark backgrounds</p>
            <p>For light backgrounds</p>
          </div>

          <h3 className="brand-card-title">Logo Variations</h3>
          <div className="brand-logo-variations">
            <div className="brand-logo-variation">
              <div className="brand-logo-variation-display brand-logo-dark">
                <div className="brand-logo-text-med">rentid</div>
              </div>
              <div className="brand-logo-variation-label">
                <p>Full Color (Dark BG)</p>
              </div>
            </div>
            <div className="brand-logo-variation">
              <div className="brand-logo-variation-display brand-logo-variation-light">
                <div className="brand-logo-text-med brand-logo-text-dark">rentid</div>
              </div>
              <div className="brand-logo-variation-label">
                <p>Monochrome Dark</p>
              </div>
            </div>
            <div className="brand-logo-variation">
              <div className="brand-logo-variation-display brand-logo-dark">
                <div className="brand-logo-text-med brand-logo-text-white">rentid</div>
              </div>
              <div className="brand-logo-variation-label">
                <p>Monochrome Light</p>
              </div>
            </div>
          </div>
          
          <a 
            href="https://drive.google.com/drive/u/1/folders/1ZeRG1v5WYe_eHni5p4HweYp6dy8k15a6"
            target="_blank"
            rel="noopener noreferrer"
            className="brand-cta-button"
          >
            ACCESS BRAND LOGO
          </a>
        </div>

        <div className="brand-card">
          <h3 className="brand-card-title">Clear Space</h3>
          <div className="brand-clear-space-demo">
            <div className="brand-clear-space-border">
              <div className="brand-clear-space-logo">
                <div className="brand-logo-text-med">rentid</div>
              </div>
            </div>
          </div>
          <p className="brand-text-center brand-text-sm">
            Maintain minimum clear space around the logo equal to the height of the "r" in rentid.
          </p>
        </div>

        <div className="brand-card">
          <h3 className="brand-card-title">Logo Usage</h3>
          <div className="brand-usage-grid">
            <div className="brand-usage-box brand-usage-do">
              <span className="brand-usage-label">Do</span>
              <ul className="brand-usage-list">
                <li>
                  <div className="check-icon">✓</div>
                  <span>Use approved logo files only</span>
                </li>
                <li>
                  <div className="check-icon">✓</div>
                  <span>Maintain proper clear space</span>
                </li>
                <li>
                  <div className="check-icon">✓</div>
                  <span>Use on appropriate backgrounds</span>
                </li>
                <li>
                  <div className="check-icon">✓</div>
                  <span>Scale proportionally</span>
                </li>
                <li>
                  <div className="check-icon">✓</div>
                  <span>Use vector formats when possible</span>
                </li>
                <li>
                  <div className="check-icon">✓</div>
                  <span>Ensure sufficient contrast</span>
                </li>
              </ul>
            </div>
            <div className="brand-usage-box brand-usage-dont">
              <span className="brand-usage-label">Don't</span>
              <ul className="brand-usage-list">
                <li>
                  <div className="cross-icon">✕</div>
                  <span>Stretch or distort the logo</span>
                </li>
                <li>
                  <div className="cross-icon">✕</div>
                  <span>Change the logo colors</span>
                </li>
                <li>
                  <div className="cross-icon">✕</div>
                  <span>Add effects like shadows or gradients</span>
                </li>
                <li>
                  <div className="cross-icon">✕</div>
                  <span>Place on busy backgrounds</span>
                </li>
                <li>
                  <div className="cross-icon">✕</div>
                  <span>Use brand green on white backgrounds</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="brand-card">
          <h3 className="brand-card-title">Product Logos</h3>
          <p className="brand-text">
            Each product has vertical and horizontal logo variants, in both dark-background (white text) and light-background (black text) versions.
          </p>
          
          <div className="brand-product-logos">
            {productLogos.map((product, index) => (
              <div key={index} className="brand-product-logo-section">
                <p className="brand-product-logo-name">{product.name}</p>
                <div className="brand-product-logo-grid">
                  <div className="brand-product-logo-box brand-logo-dark">
                    <img src={product.darkBg} alt={`${product.name} vertical dark`} className="brand-product-logo-img" />
                  </div>
                  <div className="brand-product-logo-box brand-product-logo-box-light">
                    <img src={product.lightBg} alt={`${product.name} vertical light`} className="brand-product-logo-img" />
                  </div>
                  <div className="brand-product-logo-box brand-logo-dark">
                    {product.darkBgHorizontal ? (
                      <img src={product.darkBgHorizontal} alt={`${product.name} horizontal dark`} className="brand-product-logo-img-horiz" />
                    ) : (
                      <span className="brand-na-text">N/A</span>
                    )}
                  </div>
                  <div className="brand-product-logo-box brand-product-logo-box-light">
                    {product.lightBgHorizontal ? (
                      <img src={product.lightBgHorizontal} alt={`${product.name} horizontal light`} className="brand-product-logo-img-horiz" />
                    ) : (
                      <span className="brand-na-text">N/A</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <a 
            href="https://drive.google.com/drive/u/1/folders/1uSbHbEJtNAg_JgyhW1vkqTF_Dr3gK8k-"
            target="_blank"
            rel="noopener noreferrer"
            className="brand-cta-button"
          >
            ACCESS PRODUCT LOGOS
          </a>
        </div>
      </div>
    </section>
  );
};

// Naming Section
const NamingSection = () => {
  return (
    <section id="naming" className="brand-section">
      <div className="brand-container">
        <div className="brand-card">
          <span className="brand-eyebrow">Naming</span>
          <h2 className="brand-subsection-title">Brand & Product Names</h2>
          <p className="brand-text-lg">
            Consistent naming builds recognition and trust. Use these naming conventions across all communications.
          </p>
          
          <h3 className="brand-card-title">Brand Name</h3>
          <div className="brand-naming-grid">
            <div className="brand-naming-box brand-naming-correct">
              <span className="brand-naming-label">✓ Correct</span>
              <p className="brand-naming-example">Rentid</p>
              <p className="brand-naming-example">rentid</p>
              <p className="brand-text-sm">Lowercase or title case, one word, no space</p>
            </div>
            <div className="brand-naming-box brand-naming-incorrect">
              <span className="brand-naming-label">✕ Incorrect</span>
              <p className="brand-naming-example">RentID</p>
              <p className="brand-naming-example">Rent ID</p>
              <p className="brand-naming-example">Rent id</p>
              <p className="brand-text-sm">Do not capitalize ID, add spaces, or mix cases</p>
            </div>
          </div>

          <h3 className="brand-card-title">Product Names</h3>
          <div className="brand-product-names">
            <div className="brand-product-name-item">
              <h4 className="brand-product-name-title">RentDirect™</h4>
              <p className="brand-text-sm"><strong>Correct:</strong> RentDirect, RentDirect™</p>
              <p className="brand-text-sm"><strong>Incorrect:</strong> Rent Direct, rentdirect</p>
            </div>
            <div className="brand-product-name-item">
              <h4 className="brand-product-name-title">RentID™</h4>
              <p className="brand-text-sm"><strong>Correct:</strong> RentID, RentID™</p>
              <p className="brand-text-sm"><strong>Incorrect:</strong> Rent ID, rentID, rentid</p>
            </div>
            <div className="brand-product-name-item">
              <h4 className="brand-product-name-title">RentShield™</h4>
              <p className="brand-text-sm"><strong>Correct:</strong> RentShield, RentShield™</p>
              <p className="brand-text-sm"><strong>Incorrect:</strong> Rent Shield, rentshield</p>
            </div>
            <div className="brand-product-name-item">
              <h4 className="brand-product-name-title">RentAI™</h4>
              <p className="brand-text-sm"><strong>Correct:</strong> RentAI, RentAI™</p>
              <p className="brand-text-sm"><strong>Incorrect:</strong> Rent AI, rentAI, RentAi</p>
            </div>
            <div className="brand-product-name-item">
              <h4 className="brand-product-name-title">RentSuccess™</h4>
              <p className="brand-text-sm"><strong>Correct:</strong> RentSuccess, RentSuccess™</p>
              <p className="brand-text-sm"><strong>Incorrect:</strong> Rent Success, rentsuccess</p>
            </div>
            <div className="brand-product-name-item">
              <h4 className="brand-product-name-title">RentAcademy™</h4>
              <p className="brand-text-sm"><strong>Correct:</strong> RentAcademy, RentAcademy™</p>
              <p className="brand-text-sm"><strong>Incorrect:</strong> Rent Academy, rentacademy</p>
            </div>
          </div>

          <div className="brand-naming-note">
            <h4 className="brand-naming-note-title">Trademark Usage</h4>
            <p className="brand-text">
              All product names are trademarks (™). While the trademark symbol is optional in most contexts, include it in formal documents, press releases, and legal materials on first mention.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Color Section (Due to length, creating a simplified version)
const ColorSection = () => {
  const brandColors = [
    {
      name: "Primary",
      hex: "#121212",
      rgb: "18, 18, 18",
      usage: "Primary text, backgrounds, and key UI elements"
    },
    {
      name: "Secondary",
      hex: "#FCFCFC",
      rgb: "252, 252, 252",
      usage: "Backgrounds, cards, and light surfaces"
    }
  ];

  const accentColors = [
    {
      name: "Green",
      hex: "#33F2BE",
      rgb: "51, 247, 190",
      usage: "Buttons, highlights, and accent text on dark backgrounds"
    },
    {
      name: "Blue",
      hex: "#210493",
      rgb: "33, 4, 147",
      usage: "Buttons, highlights, and accent text on light backgrounds"
    }
  ];

  return (
    <section id="color" className="brand-section">
      <div className="brand-container">
        <div className="brand-card">
          <span className="brand-eyebrow">Color</span>
          <h2 className="brand-subsection-title">Color Palette</h2>
          <p className="brand-text-lg">
            Our color palette is minimal and purposeful. It reflects our brand's modern, trustworthy, and infrastructure-minded personality.
          </p>
        </div>

        <div className="brand-card">
          <h3 className="brand-card-title">Brand Colors</h3>
          <div className="brand-colors-grid">
            {brandColors.map((color, index) => (
              <div key={index} className="brand-color-card">
                <div className="brand-color-swatch" style={{ backgroundColor: color.hex }} />
                <div className="brand-color-info">
                  <h4 className="brand-color-name">{color.name}</h4>
                  <p className="brand-color-hex"><strong>HEX:</strong> {color.hex}</p>
                  <p className="brand-color-rgb"><strong>RGB:</strong> {color.rgb}</p>
                  <p className="brand-color-usage">{color.usage}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="brand-card">
          <h3 className="brand-card-title">Accent Colors</h3>
          <div className="brand-colors-grid">
            {accentColors.map((color, index) => (
              <div key={index} className="brand-color-card">
                <div className="brand-color-swatch" style={{ backgroundColor: color.hex }} />
                <div className="brand-color-info">
                  <h4 className="brand-color-name">{color.name}</h4>
                  <p className="brand-color-hex"><strong>HEX:</strong> {color.hex}</p>
                  <p className="brand-color-rgb"><strong>RGB:</strong> {color.rgb}</p>
                  <p className="brand-color-usage">{color.usage}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="brand-card">
          <h3 className="brand-card-title">Color Usage Rules</h3>
          
          <div className="brand-warning-box">
            <div className="brand-warning-icon">⚠</div>
            <div>
              <h4 className="brand-warning-title">Important: Brand Green Text Usage</h4>
              <p className="brand-warning-text">
                Brand green (#33F2BE) text should never be used on white or light backgrounds. Always use green text on dark backgrounds (#121212) for proper contrast. Note: Green containers/backgrounds on white surfaces are acceptable.
              </p>
            </div>
          </div>

          <div className="brand-usage-examples">
            <div className="brand-usage-example brand-usage-correct-bg">
              <div className="brand-usage-example-badge">✓ Correct</div>
              <div className="brand-usage-example-content">
                <span className="brand-usage-green-text">rentid</span>
              </div>
              <p className="brand-usage-example-label">Green on dark background</p>
            </div>
            <div className="brand-usage-example brand-usage-incorrect-bg">
              <div className="brand-usage-example-badge brand-usage-incorrect-badge">✕ Incorrect</div>
              <div className="brand-usage-example-content">
                <span className="brand-usage-green-text-wrong">rentid</span>
                <div className="brand-usage-strikethrough"></div>
              </div>
              <p className="brand-usage-example-label">Green on light background</p>
            </div>
          </div>

          <div className="brand-warning-box">
            <div className="brand-warning-icon">⚠</div>
            <div>
              <h4 className="brand-warning-title">Important: Font Weight on Light Backgrounds</h4>
              <p className="brand-warning-text">
                Never use light-weighted fonts or thin icons on white or light backgrounds. Always use medium to bold font weights for proper readability and accessibility compliance.
              </p>
            </div>
          </div>
        </div>

        <div className="brand-card">
          <h3 className="brand-card-title">Accessibility</h3>
          <div className="brand-accessibility-grid">
            <div className="brand-accessibility-card brand-accessibility-dark">
              <p className="brand-accessibility-rating">AA</p>
              <p className="brand-accessibility-label">Primary on Secondary</p>
            </div>
            <div className="brand-accessibility-card brand-accessibility-dark">
              <p className="brand-accessibility-rating brand-accessibility-green">AA</p>
              <p className="brand-accessibility-label brand-accessibility-green">Accent on Primary</p>
            </div>
            <div className="brand-accessibility-card brand-accessibility-green-bg">
              <p className="brand-accessibility-rating">AAA</p>
              <p className="brand-accessibility-label">Primary on Accent</p>
            </div>
          </div>
          <p className="brand-text-center brand-text-sm">
            All color combinations meet WCAG 2.1 accessibility standards.
          </p>
        </div>
      </div>
    </section>
  );
};

// Typography Section
const TypographySection = () => {
  return (
    <section id="typography" className="brand-section">
      <div className="brand-container">
        <div className="brand-card">
          <span className="brand-eyebrow">Typography</span>
          <h2 className="brand-subsection-title">Typography</h2>
          <p className="brand-text-lg">
            Our typography is clean, modern, and highly readable. We use Inter as our primary typeface across all digital experiences.
          </p>
          
          <h3 className="brand-card-title">Primary Typeface</h3>
          <div className="brand-typography-showcase">
            <h1 className="brand-typography-display">Inter</h1>
            <p className="brand-text">
              Inter is a carefully crafted typeface designed for computer screens. It features a tall x-height to aid in readability of mixed-case and lower-case text.
            </p>
          </div>

          <h3 className="brand-card-title">Font Weights</h3>
          <div className="brand-typography-weights">
            <div className="brand-typography-weight-item">
              <p className="brand-typography-regular">Regular (400)</p>
              <p className="brand-text-sm">Body text, descriptions</p>
            </div>
            <div className="brand-typography-weight-item">
              <p className="brand-typography-medium">Medium (500)</p>
              <p className="brand-text-sm">Subheadings, labels</p>
            </div>
            <div className="brand-typography-weight-item">
              <p className="brand-typography-semibold">Semibold (600)</p>
              <p className="brand-text-sm">Buttons, emphasis</p>
            </div>
            <div className="brand-typography-weight-item">
              <p className="brand-typography-bold">Bold (700)</p>
              <p className="brand-text-sm">Headings, titles</p>
            </div>
          </div>

          <h3 className="brand-card-title">Type Scale</h3>
          <div className="brand-type-scale">
            <div className="brand-type-scale-item">
              <h1 className="brand-type-h1">Heading 1</h1>
              <p className="brand-text-sm">48px / Bold</p>
            </div>
            <div className="brand-type-scale-item">
              <h2 className="brand-type-h2">Heading 2</h2>
              <p className="brand-text-sm">36px / Bold</p>
            </div>
            <div className="brand-type-scale-item">
              <h3 className="brand-type-h3">Heading 3</h3>
              <p className="brand-text-sm">28px / Semibold</p>
            </div>
            <div className="brand-type-scale-item">
              <p className="brand-type-body">Body Text</p>
              <p className="brand-text-sm">16px / Regular</p>
            </div>
            <div className="brand-type-scale-item">
              <p className="brand-type-small">Small Text</p>
              <p className="brand-text-sm">14px / Regular</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Buttons Section
const ButtonsSection = () => {
  return (
    <section id="buttons" className="brand-section">
      <div className="brand-container">
        <div className="brand-card">
          <span className="brand-eyebrow">Buttons</span>
          <h2 className="brand-subsection-title">Button Styles</h2>
          <p className="brand-text-lg">
            Our buttons are clear, accessible, and purposeful. They guide users through actions with confidence.
          </p>
          
          <h3 className="brand-card-title">Primary Buttons</h3>
          <div className="brand-buttons-showcase">
            <button className="brand-button brand-button-primary">Primary Button</button>
            <button className="brand-button brand-button-primary-dark">Primary Dark</button>
          </div>
          <p className="brand-text-sm">Use for primary actions and key CTAs</p>

          <h3 className="brand-card-title">Secondary Buttons</h3>
          <div className="brand-buttons-showcase">
            <button className="brand-button brand-button-secondary">Secondary Button</button>
            <button className="brand-button brand-button-outline">Outline Button</button>
          </div>
          <p className="brand-text-sm">Use for secondary actions and alternative options</p>

          <h3 className="brand-card-title">Button States</h3>
          <div className="brand-buttons-states">
            <div className="brand-button-state">
              <button className="brand-button brand-button-primary">Default</button>
              <p className="brand-text-sm">Normal state</p>
            </div>
            <div className="brand-button-state">
              <button className="brand-button brand-button-primary brand-button-hover">Hover</button>
              <p className="brand-text-sm">On hover</p>
            </div>
            <div className="brand-button-state">
              <button className="brand-button brand-button-primary" disabled>Disabled</button>
              <p className="brand-text-sm">Inactive state</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Compliance Section
const ComplianceSection = () => {
  const checklistItems = [
    "Brand name formatted correctly",
    "Product names formatted correctly",
    "Positioning language accurate",
    "Visuals reflect rental operations",
    "Messaging focuses on operators",
    "Never use light-weighted fonts or icons on white backgrounds",
    "Always follow accessibility rules"
  ];

  return (
    <section id="compliance" className="brand-section">
      <div className="brand-container">
        <div className="brand-card brand-card-dark">
          <h2 className="brand-section-title brand-section-title-white">Compliance Checklist</h2>
          
          <div className="brand-compliance-grid">
            <div>
              <h3 className="brand-compliance-title">Why Compliance Matters</h3>
              <p className="brand-compliance-text">
                Brand consistency builds trust with operators and partners. Following these guidelines ensures Rentid is represented accurately across all touchpoints.
              </p>
              <ul className="brand-compliance-list">
                <li><span className="bullet-white"></span>Maintains professional credibility</li>
                <li><span className="bullet-white"></span>Ensures legal trademark protection</li>
                <li><span className="bullet-white"></span>Strengthens brand recognition</li>
                <li><span className="bullet-white"></span>Aligns messaging across teams</li>
              </ul>
            </div>
            
            <div>
              <h3 className="brand-compliance-title">Brand Compliance Checklist</h3>
              <p className="brand-compliance-text">Before publishing materials, confirm:</p>
              <ul className="brand-compliance-checklist">
                {checklistItems.map((item, index) => (
                  <li key={index}>
                    <CheckCircle className="brand-check-icon" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => {
  return (
    <section className="brand-section">
      <div className="brand-container">
        <div className="brand-cta-section">
          <h2 className="brand-cta-title">Ready to Get Started?</h2>
          <p className="brand-cta-text">
            For questions about these brand guidelines or to request additional assets, contact our marketing team.
          </p>
          <div className="brand-cta-buttons">
            <a href="/contact-us" className="brand-button brand-button-primary-large">
              Contact Us
            </a>
            <a 
              href="https://drive.google.com/drive/u/1/folders/1ZeRG1v5WYe_eHni5p4HweYp6dy8k15a6"
              target="_blank"
              rel="noopener noreferrer"
              className="brand-button brand-button-secondary-large"
            >
              Download Assets
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Brand Footer (custom for this page to avoid sidebar overlap)
const BrandFooter = () => {
  return (
    <footer className="brand-footer">
      <div className="brand-container">
        <div className="brand-footer-content">
          <p>&copy; {new Date().getFullYear()} Rentid, Inc. All rights reserved.</p>
          <div className="brand-footer-links">
            <a href="/privacy">Privacy</a>
            <a href="/terms">Terms</a>
            <a href="/accessibility-statement">Accessibility</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default BrandGuidelines;
